import { useEffect } from 'react';
import {
  resolveDeepLink,
  logResolutionFailure,
  type SignalSectionId,
} from '@/lib/deep-link-resolution';
import type { ModelSchema } from '@arcanum-hq/shared-types';

/**
 * `agent-ux-optimization` Phase 6 Session 8 (2026-05-08).
 *
 * Mount inside the Signal variant body. Watches `?section=` and
 * `?assumption=` URL params and dispatches the matching DOM action:
 *
 *   - `?section=hero|property|story|comparison|assumptions` →
 *     `document.getElementById('signal-section-${id}').scrollIntoView()`.
 *   - `?assumption=<namedRange>` → flash-highlights the matching
 *     `<AssumptionCard>` (`[data-testid="assumption-card"][data-named-range="<namedRange>"]`)
 *     for ~1.2s by toggling the `data-flash` attribute. The card's CSS
 *     listens for the attribute and applies a transient ring + bg
 *     pulse. If the card isn't currently rendered (the assumption is
 *     on a different pagination page), the listener silently no-ops —
 *     URL bookmarks remain valid even when the target isn't visible.
 *
 * Listener consumes the URL params on mount + whenever they change, then
 * fires `onConsume()` so the parent route drops the param from the URL
 * (preventing re-fire on navigation back). Mirrors Phase 3 Session 5's
 * `?cell=` / `?namedRange=` listener pattern.
 *
 * SSR-safe: every DOM access is guarded; the listener no-ops in
 * environments without `document` / `window`.
 */
const FLASH_DURATION_MS = 1200;
const SCROLL_DEFER_MS = 50;

export interface SignalAnchorListenerProps {
  section?: SignalSectionId;
  assumption?: string;
  modelSchema: ModelSchema | null;
  onConsume?: () => void;
}

export function SignalAnchorListener({
  section,
  assumption,
  modelSchema,
  onConsume,
}: SignalAnchorListenerProps) {
  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (!section && !assumption) return;

    // No mount-scoped dedupe: React strict-mode runs effect → cleanup →
    // effect again. The cleanup below clears the pending timer so only
    // the surviving timer fires its action exactly once. After
    // `onConsume()` runs, the URL drops `?section=` / `?assumption=`,
    // and the early-return at line ~62 handles subsequent re-renders
    // (gap-analysis 2026-05-08 fix — the prior `lastConsumedRef`
    // implementation bailed out on the second strict-mode invocation
    // BEFORE either action or consume ran, causing the scroll + URL
    // cleanup to silently no-op on initial mount).

    if (section) {
      const resolution = resolveDeepLink(
        { surface: 'signal', section },
        modelSchema ?? ({ inputs: [], outputs: [] } as unknown as ModelSchema),
      );
      if (!resolution.ok) {
        logResolutionFailure('signal', resolution);
        onConsume?.();
        return;
      }
      // Defer one tick so the variant body has finished mounting + the
      // section <section id="…"> nodes are in the DOM. Without this, a
      // cold-load `?section=assumptions` can fire before the assumption
      // grid mounts.
      const id = `signal-section-${section}`;
      const timer = window.setTimeout(() => {
        const node = document.getElementById(id);
        if (node) {
          node.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } else {
          logResolutionFailure('signal', {
            ok: false,
            reason: 'unknown_section',
            param: section,
          });
        }
        onConsume?.();
      }, SCROLL_DEFER_MS);
      return () => window.clearTimeout(timer);
    }

    if (assumption) {
      const resolution = resolveDeepLink(
        { surface: 'signal', assumption },
        modelSchema ?? ({ inputs: [], outputs: [] } as unknown as ModelSchema),
      );
      if (!resolution.ok) {
        logResolutionFailure('signal', resolution);
        onConsume?.();
        return;
      }
      const timer = window.setTimeout(() => {
        const node = document.querySelector<HTMLElement>(
          `[data-testid="assumption-card"][data-named-range="${cssEscape(assumption)}"]`,
        );
        if (!node) {
          // Card not on the current page — silent no-op + consume so the
          // param isn't re-fired forever. Bookmarks remain valid; the
          // user can paginate to the right page manually.
          onConsume?.();
          return;
        }
        node.scrollIntoView({ behavior: 'smooth', block: 'center' });
        node.setAttribute('data-flash', 'true');
        const flashTimer = window.setTimeout(() => {
          node.removeAttribute('data-flash');
        }, FLASH_DURATION_MS);
        onConsume?.();
        // Cleanup is best-effort; the inner timer is captured in the
        // closure but unmount during the flash window is rare.
        void flashTimer;
      }, SCROLL_DEFER_MS);
      return () => window.clearTimeout(timer);
    }

    return undefined;
  }, [section, assumption, modelSchema, onConsume]);

  // Listener is purely effect-based; no rendered output.
  return null;
}

/**
 * `CSS.escape` polyfill — vitest's jsdom environment carries `CSS.escape`
 * but defensive guard for older runtimes. Escapes attribute selector
 * values so a namedRange with special characters (unlikely but possible)
 * doesn't break the querySelector.
 */
function cssEscape(value: string): string {
  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
    return CSS.escape(value);
  }
  return value.replace(/(["\\])/g, '\\$1');
}
